import { z } from "zod";

const partySchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  company: z.string().min(2, "Company name required").max(100),
  address: z.string().min(5, "Address required").max(200),
  city: z.string().min(2, "City required").max(100),
  email: z.string().email("Valid email required"),
  phone: z.string().min(5, "Phone required").max(30),
  signature: z.string().optional(),
});

export const loiSchema = z.object({
  // Parties
  buyer: partySchema,
  seller: partySchema,
  
  // Target Business
  businessName: z.string().min(2, "Business name required").max(200),
  businessDescription: z.string().min(10, "Description required").max(2000),
  businessAddress: z.string().min(5, "Business address required").max(300),
  
  // Purchase Terms
  purchasePrice: z.number().min(0, "Purchase price required"),
  currency: z.enum(["USD", "CAD", "EUR"]),
  depositAmount: z.number().min(0, "Deposit amount required"),
  depositPercentage: z.number().min(0).max(100).optional(),
  
  // Payment Structure
  paymentStructure: z.object({
    cashAtClosing: z.number().min(0),
    sellerFinancing: z.number().min(0),
    earnout: z.number().min(0),
    other: z.number().min(0),
    description: z.string().max(1000).optional(),
  }),
  
  // Timeline
  dueDiligenceDays: z.number().min(1, "Due diligence period required").max(365),
  closingDays: z.number().min(1, "Closing period required").max(365),
  exclusivityDays: z.number().min(0).max(365),
  
  // Conditions
  conditions: z.array(z.string()).min(1, "At least one condition required"),
  
  // Assets Included
  assetsIncluded: z.array(z.string()),
  assetsExcluded: z.array(z.string()).optional(),
  
  // Employment
  employeeRetention: z.boolean(),
  sellerNonCompete: z.boolean(),
  nonCompeteYears: z.number().min(0).max(10).optional(),
  nonCompeteRadius: z.string().max(100).optional(),
  sellerTransitionPeriod: z.number().min(0).max(24).optional(),
  
  // Legal
  applicableLaw: z.string().min(2, "Applicable law required").max(100),
  arbitration: z.boolean(),
  confidential: z.boolean(),
  bindingIntent: z.boolean(),
  
  // Expiration
  expirationDays: z.number().min(1, "Expiration period required").max(90),
});

export type LOIFormData = z.infer<typeof loiSchema>;
export type LOIParty = z.infer<typeof partySchema>;

// Default conditions for LOI
export const defaultConditions = [
  "Satisfactory completion of due diligence",
  "Buyer obtaining financing approval",
  "Review and approval of all contracts and leases",
  "Verification of financial statements",
  "No material adverse change in business operations",
  "Approval by buyer's legal counsel",
  "Seller providing all requested documentation",
];

// Default assets typically included
export const defaultAssetsIncluded = [
  "All inventory",
  "Furniture, fixtures, and equipment",
  "Customer lists and databases",
  "Intellectual property and trademarks",
  "Domain names and websites",
  "Phone numbers and email addresses",
  "Goodwill",
  "Leasehold improvements",
  "Accounts receivable (as negotiated)",
];
